// NEW_FILE_JUSTIFIED: Selectable insurance type card for the Apply for Insurance page — unique card-with-CTA pattern not covered by existing components

import { ArrowRight, Car, Check, HeartPulse, Home, Plane, Shield } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IInsuranceTypeCardProps } from '@/@types/insurance';

const ICON_MAP: Record<string, LucideIcon> = {
  'heart-pulse': HeartPulse,
  car: Car,
  plane: Plane,
  house: Home,
  shield: Shield,
};

export function InsuranceTypeCard({
  name,
  description,
  iconName,
  iconColor,
  iconBgColor,
  features,
  isPopular = false,
  onSelect,
  disabled = false,
}: IInsuranceTypeCardProps) {
  const Icon = ICON_MAP[iconName] ?? Shield;

  return (
    <div
      className={cn(
        'flex flex-col rounded-2xl border border-border bg-card overflow-hidden',
        disabled && 'opacity-60'
      )}
    >
      {/* Top section */}
      <div className="flex flex-col gap-4 p-6">
        {/* Icon + badge row */}
        <div className="flex items-start justify-between">
          <div
            className="flex h-12 w-12 items-center justify-center rounded-xl shrink-0"
            style={{ backgroundColor: iconBgColor }}
          >
            <Icon size={24} style={{ color: iconColor }} strokeWidth={1.75} />
          </div>
          {isPopular && (
            <span className="rounded-full bg-accent/20 px-3 py-1 text-xs font-medium text-accent-foreground">
              Most Popular
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-foreground">{name}</h3>

        {/* Description */}
        <p className="text-[13px] font-normal leading-relaxed text-muted-foreground">
          {description}
        </p>

        {/* Features list */}
        {features.length > 0 && (
          <ul className="flex flex-col gap-2">
            {features.map(feature => (
              <li key={feature} className="flex items-start gap-2">
                <Check
                  size={14}
                  className="mt-0.5 shrink-0"
                  style={{ color: iconColor }}
                  strokeWidth={2.5}
                />
                <span className="text-[12px] text-muted-foreground">{feature}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Bottom CTA section */}
      <div className="mt-auto border-t border-border px-6 py-4">
        <button
          type="button"
          disabled={disabled}
          onClick={onSelect}
          className={cn(
            'flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors',
            isPopular
              ? 'bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90 disabled:cursor-not-allowed disabled:opacity-50'
              : 'border border-border bg-card text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50'
          )}
        >
          Start Application
          <ArrowRight size={16} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
